import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ButtonComponent } from '@app/components/button';
import { CardComponent } from '@app/components/card';
import { FormActionButtonComponent } from '@app/components/form-action-button';
import { SelectDropdownComponent } from '@app/components/select-dropdown';
import { ToastService, ToastType } from '@app/components/toast';
import { Logger } from '@app/services/logger/logger.service';
import { TranslatePipe } from '@ngx-translate/core';
import { EnterpriseTenantContract } from '@skyfit/core-data-library';
import moment from 'moment';
import { AdminTenantService } from '../../../services/admin-tenant/admin-tenant.service';
import { SaveChangesService } from '../../../services/save-changes/save-changes.service';
import { TranslatorService } from '../../../services/translator/translator.service';
import { TenantConfigurationService } from '../tenant-configuration.service';
import { ContractDialogComponent } from './contract-dialog/contract-dialog.component';
import { ContractFormComponent } from './contract-form/contract-form.component';

const enumValidator = (enumValues: string[]): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    return enumValues.includes(value) ? null : { enum: true };
  };
};

export const startDateAfterEndDateValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const startDate = control.value;
    const endDate = control.parent?.get('expiration')?.value;

    if (endDate && startDate && moment(startDate).isAfter(moment(endDate))) {
      return { startDateAfterEndDate: true };
    }

    return null;
  };
};

export const twoDecimalPlacesValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const isValid = /^\d+(\.\d{2})?$/.test(value);

    if (value && !isValid) {
      return { invalidDecimal: true };
    }

    return null;
  };
};

const endDateBeforeStartDateValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const endDate = control.value;
    const startDate = control.parent?.get('start')?.value;

    if (endDate && startDate && moment(endDate).isBefore(moment(startDate))) {
      return { endDateBeforeStartDate: true };
    }

    return null;
  };
};

@Component({
  selector: 'app-contracts',
  standalone: true,
  imports: [
    CardComponent,
    FormActionButtonComponent,
    ButtonComponent,
    ContractFormComponent,
    SelectDropdownComponent,
    TranslatePipe,
    ContractDialogComponent,
    CommonModule,
  ],
  templateUrl: './contracts.component.html',
  styleUrls: ['./contracts.component.css'],
})
export class ContractsComponent implements OnInit {
  private readonly adminTenantService = inject(AdminTenantService);
  private readonly toastService = inject(ToastService);
  private readonly translatorService = inject(TranslatorService);
  private readonly saveChangesService = inject(SaveChangesService);
  private readonly logger = inject(Logger);
  private tenantConfigurationService = inject(TenantConfigurationService);
  dialogOpen = this.tenantConfigurationService.isAddNewContractDialogOpen;
  contract = signal(this.adminTenantService.tenant()?.contracts?.[0]);
  hasContracts = computed(
    () => !!this.adminTenantService.tenant()?.contracts?.length
  );
  isReadMode = this.tenantConfigurationService.isReadMode;
  contractOptions = computed(() => {
    const contracts = this.adminTenantService.tenant()?.contracts;
    if (!contracts) {
      return [];
    }

    return contracts.map((contract) => ({
      label: `${moment(contract.start).format('L')} - ${moment(
        contract.expiration
      ).format('L')}`,
      value: contract._id,
    }));
  });

  isFormValid(): boolean {
    return true;
  }

  ngOnInit(): void {
    const contracts = this.adminTenantService.tenant()?.contracts;
    if (contracts && contracts.length > 1) {
      const sortedContracts = [...contracts].sort((a, b) => {
        return moment(b.start).diff(moment(a.start));
      });
      this.contract.set(sortedContracts[0]);
    }
  }

  public handleEdit(): void {
    if (this.isReadMode()) {
      this.isReadMode.set(false);
      this.saveChangesService.setHasChanges(true);
    } else {
      this.onSubmit();
    }
  }

  public handleContractSelection(selectedContractId: string): void {
    this.contract.set(
      this.adminTenantService
        .tenant()
        .contracts.find((contract) => contract._id === selectedContractId)
    );
  }

  public addNewContract(): void {
    this.dialogOpen.set(true);
  }

  closeDialog() {
    this.tenantConfigurationService.toggleAddNewContractDialog();
  }

  public saveContract(contract: EnterpriseTenantContract): void {
    this.adminTenantService.updateTenantContracts(contract).subscribe({
      next: (updatedTenant) => {
        this.saveChangesService.setHasChanges(false);
        this.closeDialog();
        this.contract.set(updatedTenant.contracts?.[0]);
      },
      error: (error) => {
        this.logger.error(
          `[ContractsComponent] Error creating contract for tenant '${
            this.adminTenantService.tenant()?.id
          }': ${error.message}`
        );

        const message = this.translatorService.translate(
          'tenantConfiguration.general.updateError'
        );
        this.toastService.add(message, ToastType.ERROR);
      },
    });
  }

  public onSubmit(): void {
    if (this.isFormValid()) {
      this.adminTenantService.updateTenantContracts(this.contract()).subscribe({
        next: (updatedTenant) => {
          this.saveChangesService.setHasChanges(false);
          this.isReadMode.set(true);
          const updatedContract = updatedTenant.contracts?.find(
            (c) => c._id === this.contract()?._id
          );
          if (updatedContract) {
            this.contract.set(updatedContract);
          }
        },
        error: (error) => {
          this.logger.error(
            `[ContractsComponent] Error updating contract for tenant '${
              this.adminTenantService.tenant()?.id
            }': ${error.message}`
          );

          const message = this.translatorService.translate(
            'tenantConfiguration.general.updateError'
          );
          this.toastService.add(message, ToastType.ERROR);
        },
      });
    } else {
      const message = this.translatorService.translate(
        'TENANT_CONFIGURATION.GENERAL.FILL_ALL_REQUIRED_FIELDS'
      );
      this.toastService.add(message, ToastType.ERROR);
    }
  }
}
